import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Download, Smartphone } from 'lucide-react';
import { APP_NAME } from '../constants/appName';

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  // Feb 2026 — auto-hide when any Radix Dialog is open so this prompt
  // never overlaps modals like the Lucky Draw Super Final wheel.
  const [dialogOpen, setDialogOpen] = useState(false);
  useEffect(() => {
    const check = () => {
      const anyOpen = document.body.hasAttribute('data-scroll-locked') ||
        !!document.querySelector('[role="dialog"][data-state="open"]');
      setDialogOpen(anyOpen);
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { attributes: true, childList: true, subtree: true, attributeFilter: ['data-state', 'data-scroll-locked'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Detect iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    // Check if user dismissed the prompt recently (within 24 hours)
    const dismissedAt = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt);
      const twentyFourHours = 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedTime < twentyFourHours) {
        return; // Don't show prompt
      }
    }

    // Don't show if already installed
    if (standalone) {
      return;
    }

    // Listen for the beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a delay
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS, show the prompt after a delay
    if (ios && !standalone) {
      setTimeout(() => setShowPrompt(true), 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Android/Chrome install
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('Install prompt outcome:', outcome);
      setDeferredPrompt(null);
    }
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    setShowPrompt(false);
  };

  if (!showPrompt || isStandalone || dialogOpen) {
    return null;
  }

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up"
      style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      data-testid="pwa-install-prompt"
    >
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-[#FF6B6B] to-[#FFD93D] p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <Smartphone className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-white text-lg">Install {APP_NAME}</h3>
                <p className="text-white/80 text-sm">For faster access & live updates</p>
              </div>
            </div>
            <button 
              onClick={handleDismiss}
              className="text-white/80 hover:text-white transition-colors p-1"
              data-testid="pwa-dismiss-btn"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {isIOS ? (
            <div className="space-y-3">
              <p className="text-gray-600 text-sm">
                Install this app on your iPhone:
              </p>
              <ol className="text-sm text-gray-700 space-y-2">
                <li className="flex items-center gap-2">
                  <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  Tap the <strong>Share</strong> button <span className="inline-block w-5 h-5 bg-gray-100 rounded text-center leading-5">↑</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  Scroll down and tap <strong>"Add to Home Screen"</strong>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  Tap <strong>"Add"</strong> in the top right corner
                </li>
              </ol>
              <Button 
                onClick={handleDismiss}
                className="w-full mt-4"
                variant="outline"
              >
                Got it!
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <span className="text-green-500">✓</span> Works offline
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-500">✓</span> Push notifications
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-500">✓</span> Fast loading
                </div>
              </div>
              <div className="flex gap-3">
                <Button 
                  onClick={handleDismiss}
                  variant="outline"
                  className="flex-1"
                  data-testid="pwa-not-now-btn"
                >
                  Not Now
                </Button>
                <Button 
                  onClick={handleInstall}
                  className="flex-1 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] hover:opacity-90"
                  data-testid="pwa-install-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Install
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PWAInstallPrompt;
